import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Center,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Select as ChakraSelect,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
  Text,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { useUserContext } from "../../context/UserContext";
import { useApiRequest } from "../../hooks/useApiRequest";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { usePaginatedApiRequest } from "../../hooks/usePaginatedApiRequest";
import { useApiClient } from "../../hooks/useApiClient";
import { Card } from "../../components/Card/Card";
import { ConfirmationWithInputDialog } from "../../components/ConfirmationWithInputDialog/ConfirmationWithInputDialog";
import { MemberFilterQueryModal } from "../../components/MemberFilterQueryModal/MemberFilterQueryModal";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import DateUtil from "../../util/DateUtil";
import { useToast } from "../../hooks/useToast";

export const ZeroBalanceInactiveMemberCards = () => {
  const apiClient = useApiClient();
  const business = useBusinessContext();
  const { userBusinessId } = useUserContext();
  const [program, setProgram] = useState<string>();
  const [memberFilterQuery, setMemberFilterQuery] = useState<string>();
  const memberFilterQueryModal = useDisclosure();
  const [filter, setFilter] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>([]);
  const [isConfirmCloseDialogOpen, setIsConfirmCloseDialogOpen] =
    useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    data: configurations,
    error: configurationError,
    isLoading: configLoading,
  } = useApiRequest(
    (client) => client.getConfiguration(business.BusinessId),
    [business]
  );

  const {
    items: cards,
    isLoading: reportLoading,
    isLoadingPage: reportLoadingPage,
    error: reportError,
    fetchNextPage,
    refresh,
  } = usePaginatedApiRequest(
    (client) =>
      client.getZeroBalanceInactiveMemberCardsReport(business.BusinessId, {
        limit,
        memberFilterQuery,
        cardProgramId: program,
      }),
    (client, continuationToken) =>
      client.getZeroBalanceInactiveMemberCardsReport(business.BusinessId, {
        limit,
        memberFilterQuery,
        cardProgramId: program,
        continuationToken,
      }),
    [business, filter]
  );

  const programTypeOptions = useMemo(
    () =>
      configurations?.CardPrograms.map((p) => {
        return (
          <option value={p.CardProgramId} key={p.CardProgramId}>
            {p.Description}
          </option>
        );
      }),
    [configurations]
  );

  const allLoadedCardIds = useMemo(
    () =>
      (cards ?? [])
        .map((card) => card.CardId)
        .filter((cardId): cardId is string => !!cardId),
    [cards]
  );

  const allLoadedSelected =
    allLoadedCardIds.length > 0 &&
    allLoadedCardIds.every((cardId) => selectedCardIds.includes(cardId));

  const selectedCards = useMemo(
    () =>
      (cards ?? []).filter(
        (card) => card.CardId && selectedCardIds.includes(card.CardId)
      ),
    [cards, selectedCardIds]
  );

  const toggleSelectAllLoaded = () => {
    if (allLoadedSelected) {
      setSelectedCardIds((current) =>
        current.filter((cardId) => !allLoadedCardIds.includes(cardId))
      );
      return;
    }

    setSelectedCardIds((current) =>
      Array.from(new Set([...current, ...allLoadedCardIds]))
    );
  };

  const toggleCardSelection = (cardId: string) => {
    setSelectedCardIds((current) =>
      current.includes(cardId)
        ? current.filter((id) => id !== cardId)
        : [...current, cardId]
    );
  };

  const cardRows = useMemo(
    () =>
      cards?.map((c) => {
        const formattedDate = DateUtil.getLocalDateFormatFromString(
          c.CardCreatedDateTime
        );
        return (
          <Tr key={c.CardId}>
            <Td>
              <Checkbox
                isChecked={!!c.CardId && selectedCardIds.includes(c.CardId)}
                onChange={() => c.CardId && toggleCardSelection(c.CardId)}
                isDisabled={!c.CardId || !c.ExternalAccountId}
              />
            </Td>
            <Td whiteSpace="nowrap">
              {c.MemberId ? (
                <PreviewButton
                  hover={true}
                  color="gray.600"
                  linkProps={{ target: "_self" }}
                  url={`/businesses/${business.BusinessId}/members/${c.MemberId}`}
                >
                  {c.MembershipNumber || c.MemberId}
                </PreviewButton>
              ) : (
                c.MembershipNumber
              )}
            </Td>
            <Td whiteSpace="nowrap">
              {c.FirstName} {c.LastName}
            </Td>
            <Td whiteSpace="nowrap">{c.MemberStatus || "-"}</Td>
            <Td whiteSpace="nowrap">
              {c.CardId ? (
                <PreviewButton
                  hover={true}
                  color="gray.600"
                  linkProps={{ target: "_self" }}
                  url={`/businesses/${business.BusinessId}/cards/${c.CardId}`}
                >
                  {c.ExternalAccountId || c.CardId}
                </PreviewButton>
              ) : (
                c.ExternalAccountId
              )}
            </Td>
            <Td whiteSpace="nowrap">{c.CardProgramType}</Td>
            <Td whiteSpace="nowrap">{formattedDate}</Td>
            <Td whiteSpace="nowrap">{c.AccountStatus}</Td>
            <Td whiteSpace="nowrap">${c.CurrentBalance ?? 0}</Td>
          </Tr>
        );
      }),
    [cards, selectedCardIds, business.BusinessId]
  );

  const run = () => {
    setSelectedCardIds([]);
    setFilter(filter + 1);
  };

  const onMemberFilterQuerySelected = (query?: string) => {
    setMemberFilterQuery(query || undefined);
    memberFilterQueryModal.onClose();
  };

  const resetMemberFilterQuery = () => {
    setMemberFilterQuery(undefined);
  };

  const downloadCsv = async () => {
    const qresult = apiClient.getZeroBalanceInactiveMemberCardsReportCsv(
      business.BusinessId,
      {
        memberFilterQuery,
        cardProgramId: program,
      }
    );

    qresult.then((result) => {
      if (result.ok) {
        const blobData = new Blob([result.data]);
        const url = URL.createObjectURL(blobData);
        const link = document.createElement("a");
        link.download = `zero-balance-inactive-member-cards-${business.BusinessId}.csv`;
        link.href = url;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (result.statusCode == 404) showErrorToast(result.message);
      else {
        showErrorToast("Failed to download the report");
      }
    });
  };

  const closeSelected = async () => {
    const cardsToClose = selectedCards.filter(
      (card) => card.CardId && card.ExternalAccountId
    );
    if (cardsToClose.length === 0) {
      showErrorToast("Select at least one card to close.");
      return;
    }

    setIsClosing(true);
    const result = await apiClient.bulkCloseCards(business.BusinessId, {
      Cards: cardsToClose.map((card) => ({
        CardId: card.CardId!,
        ExternalAccountId: card.ExternalAccountId!,
      })),
    });
    setIsClosing(false);
    setIsConfirmCloseDialogOpen(false);

    if (!result.ok) {
      showErrorToast(result.message || "Failed to close cards");
      return;
    }

    const closeResults = result.data.Results ?? [];
    const successes = closeResults.filter((item) => item.Success);
    const failures = closeResults.filter((item) => !item.Success);
    if (failures.length === 0) {
      showSuccessToast(`Closed ${successes.length} card(s).`);
    } else {
      showErrorToast(
        `Closed ${successes.length} card(s). ${
          failures.length
        } failed: ${failures
          .map((item) => `${item.CardId}: ${item.ErrorMessage ?? "error"}`)
          .join("; ")}`
      );
    }

    setSelectedCardIds([]);
    refresh();
  };

  return (
    <>
      <PageHeading>
        <Breadcrumb>
          {!userBusinessId && (
            <BreadcrumbItem>
              <BreadcrumbLink as={Link} to="/businesses">
                Businesses
              </BreadcrumbLink>
            </BreadcrumbItem>
          )}
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to={`/businesses/${business.BusinessId}`}>
              {business.DisplayName}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/businesses/${business.BusinessId}/reports`}
            >
              Reports
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        <PageHeading.Title>
          Zero Balance CherryPay Cards by Member
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="start" width="100%">
          <Card>
            <Flex gridGap={6} className="form-filter-wrapper" flexWrap="wrap">
              <Box alignSelf="flex-end">
                <Button
                  variant="solid"
                  colorScheme="cherryButton"
                  onClick={memberFilterQueryModal.onOpen}
                >
                  Member Filters
                </Button>
                {memberFilterQuery && (
                  <Button marginLeft="4" onClick={resetMemberFilterQuery}>
                    Reset
                  </Button>
                )}
              </Box>
              {configurations && configurations.CardPrograms.length > 0 && (
                <Box>
                  <FormControl>
                    <FormLabel>Program</FormLabel>
                    <ChakraSelect
                      placeholder="Select program"
                      onChange={(e) => setProgram(e.target.value || undefined)}
                      defaultValue={program}
                    >
                      {programTypeOptions}
                    </ChakraSelect>
                  </FormControl>
                </Box>
              )}
              {configLoading && (
                <Center>
                  <Spinner />
                </Center>
              )}
              {configurationError && (
                <ErrorMessage>
                  An error was encountered while loading the program types.
                </ErrorMessage>
              )}
              <Box>
                <FormControl>
                  <FormLabel>Members per page</FormLabel>
                  <ChakraSelect
                    onChange={(e) => setLimit(parseInt(e.target.value))}
                    value={limit}
                    width="20"
                  >
                    <option value="10">10</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="250">250</option>
                    <option value="500">500</option>
                  </ChakraSelect>
                </FormControl>
              </Box>
              <Box alignSelf="flex-end" className="form-filter-button-wrapper">
                <Button colorScheme="cherryButton" onClick={run}>
                  Run
                </Button>
                <Button marginLeft="4" onClick={downloadCsv}>
                  Download
                </Button>
                <Button
                  marginLeft="4"
                  colorScheme="cherryButton"
                  variant="outline"
                  onClick={() => setIsConfirmCloseDialogOpen(true)}
                  isDisabled={selectedCards.length === 0}
                >
                  Close selected ({selectedCards.length})
                </Button>
              </Box>
            </Flex>
            <Text mt={4} fontSize="sm" color="gray.600">
              {memberFilterQuery
                ? `Member filter: ${memberFilterQuery.replace(/^\?/, "")}. `
                : "No member filter applied - all Members will be included. "}
              Each page scans the selected number of Members and only lists
              those holding a $0 balance card, so a page may return no rows.
            </Text>
          </Card>
          <Card width="100%">
            {!cards && reportLoading && (
              <Center>
                <Spinner margin="4" />
              </Center>
            )}
            <Box width="100%">
              <TableContainer alignSelf="start" width="100%" overflowX="auto">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>
                        <Checkbox
                          isChecked={allLoadedSelected}
                          isDisabled={allLoadedCardIds.length === 0}
                          onChange={toggleSelectAllLoaded}
                        />
                      </Th>
                      <Th whiteSpace="nowrap">Member Number</Th>
                      <Th whiteSpace="nowrap">Patron Name</Th>
                      <Th whiteSpace="nowrap">Member Status</Th>
                      <Th whiteSpace="nowrap">External Account ID</Th>
                      <Th whiteSpace="nowrap">Card Program Type</Th>
                      <Th whiteSpace="nowrap">Card Created Date</Th>
                      <Th whiteSpace="nowrap">Account Status</Th>
                      <Th whiteSpace="nowrap">Current Balance</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(cards?.length && cardRows) || (
                      <Tr>
                        <Td colSpan={9} fontStyle="italic" textAlign="center">
                          No cards data found
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </Card>
          {fetchNextPage && (
            <Card>
              {!reportLoadingPage && (
                <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                  Load more...
                </Button>
              )}
              {reportLoadingPage && <Spinner />}
            </Card>
          )}
          {reportError != null && (
            <Card>
              <ErrorMessage>
                An error was encountered while getting report. {reportError}
              </ErrorMessage>
            </Card>
          )}
        </VStack>
      </PageContent>
      <MemberFilterQueryModal
        size="4xl"
        {...memberFilterQueryModal}
        businessId={business.BusinessId}
        memberQueryFilter={memberFilterQuery ?? ""}
        onSuccess={onMemberFilterQuerySelected}
      />
      <ConfirmationWithInputDialog
        isOpen={isConfirmCloseDialogOpen}
        onClose={() => setIsConfirmCloseDialogOpen(false)}
        onConfirm={closeSelected}
        title="Close selected cards"
        message={`This will close ${selectedCards.length} CherryPay card(s). Closed cards cannot be reactivated. Type CLOSE to confirm.`}
        confirmationText="CLOSE"
        inputLabel="Type CLOSE"
        confirmButtonText="Close cards"
        isLoading={isClosing}
      />
    </>
  );
};
